import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import { libraryAPI } from '../utils/api';
import { toast } from 'sonner';
import { 
  FolderOpen, 
  Upload, 
  Search, 
  FileText, 
  Trash2, 
  Eye, 
  Share2, 
  Plus,
  X,
  Download,
  Loader2,
  FolderPlus
} from 'lucide-react';

export default function DocumentLibrary() {
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category_id: '',
    file: null
  });

  // New category form state
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchCategories();
    fetchDocuments();
  }, []);

  useEffect(() => {
    fetchDocuments(selectedCategory);
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const res = await libraryAPI.getCategories();
      setCategories(res.data);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchDocuments = async (categoryId = null) => {
    try {
      setLoading(true);
      const res = await libraryAPI.getDocuments(categoryId);
      setDocuments(res.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.name || !uploadForm.category_id || !uploadForm.file) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      await libraryAPI.uploadDocument(uploadForm);
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({ name: '', description: '', category_id: '', file: null });
      fetchDocuments(selectedCategory);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      await libraryAPI.createCategory(newCategory);
      toast.success('Category created successfully');
      setShowCategoryModal(false);
      setNewCategory({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await libraryAPI.deleteDocument(docId);
      toast.success('Document deleted');
      fetchDocuments(selectedCategory);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? This will delete all documents in this category.')) return;

    try {
      await libraryAPI.deleteCategory(categoryId);
      toast.success('Category deleted');
      setSelectedCategory(null);
      fetchCategories();
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (index) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-pink-100 text-pink-700',
      'bg-teal-100 text-teal-700',
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content bg-slate-50">
        <TopHeader 
          title="Document Library" 
          subtitle="Shared repository of sample documents and templates"
        />

        <div className="flex gap-6">
          {/* Sidebar - Categories */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Categories</h3>
                <button
                  onClick={() => setShowCategoryModal(true)}
                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                  title="Add Category"
                >
                  <FolderPlus size={18} />
                </button>
              </div>

              <div className="space-y-1">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
                    selectedCategory === null
                      ? 'bg-teal-50 text-teal-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <FolderOpen size={18} />
                  All Documents
                  <span className="ml-auto text-xs bg-slate-200 px-2 py-0.5 rounded-full">
                    {documents.length}
                  </span>
                </button>

                {categories.map((cat, index) => (
                  <div key={cat.id} className="flex items-center gap-1">
                    <button
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`flex-1 text-left px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
                        selectedCategory === cat.id
                          ? 'bg-teal-50 text-teal-700 font-medium'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full ${getCategoryColor(index).split(' ')[0]}`}></div>
                      <span className="truncate">{cat.name}</span>
                    </button>
                    {!cat.is_system && (
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="p-1 text-slate-400 hover:text-red-500"
                        title="Delete Category"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Actions Bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                />
              </div>

              <button
                onClick={() => setShowUploadModal(true)}
                className="ml-4 px-4 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-all flex items-center gap-2"
              >
                <Upload size={18} />
                Upload Document
              </button>
            </div>

            {/* Documents Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-teal-500" size={32} />
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
                <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Documents Found</h3>
                <p className="text-slate-500 mb-4">
                  {selectedCategory ? 'No documents in this category yet.' : 'Start by uploading your first document.'}
                </p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700"
                >
                  Upload Document
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDocuments.map((doc, index) => (
                  <div
                    key={doc.id}
                    className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg ${getCategoryColor(categories.findIndex(c => c.id === doc.category_id))}`}>
                        <FileText size={20} />
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    <h4 className="font-semibold text-slate-900 mb-1 truncate">{doc.name}</h4>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                      {doc.description || 'No description'}
                    </p>

                    <div className="flex items-center justify-between text-xs text-slate-400 mb-4">
                      <span className="px-2 py-1 bg-slate-100 rounded">{doc.category_name}</span>
                      <span>{doc.uploaded_by_name}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedDocument(doc);
                          setShowPreviewModal(true);
                        }}
                        className="flex-1 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-all flex items-center justify-center gap-1"
                      >
                        <Eye size={16} />
                        Preview
                      </button>
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                        title="Download"
                      >
                        <Download size={18} />
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="p-2 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Upload Document</h2>
                <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Document Name *</label>
                  <input
                    type="text"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                    placeholder="e.g., SOP Template - Canada PR"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    value={uploadForm.category_id}
                    onChange={(e) => setUploadForm({ ...uploadForm, category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                    rows={3}
                    placeholder="Add a helpful description..."
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">File *</label>
                  <input
                    type="file"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                  />
                  {uploadForm.file && (
                    <p className="text-sm text-slate-500 mt-1">Selected: {uploadForm.file.name}</p>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Category Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Create Category</h2>
                <button onClick={() => setShowCategoryModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category Name *</label>
                  <input
                    type="text"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    placeholder="e.g., Employment Letters"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={newCategory.description}
                    onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    rows={2}
                    placeholder="Brief description of this category"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none resize-none"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCategory}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
                >
                  <Plus size={18} />
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreviewModal && selectedDocument && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedDocument.name}</h2>
                  <p className="text-sm text-slate-500">{selectedDocument.category_name}</p>
                </div>
                <button onClick={() => setShowPreviewModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-auto p-6">
                {selectedDocument.file_type?.startsWith('image/') ? (
                  <img
                    src={selectedDocument.file_url}
                    alt={selectedDocument.name}
                    className="max-w-full h-auto rounded-lg"
                  />
                ) : selectedDocument.file_type === 'application/pdf' ? (
                  <iframe
                    src={selectedDocument.file_url}
                    title={selectedDocument.name}
                    className="w-full h-[500px] rounded-lg border"
                  />
                ) : (
                  <div className="text-center py-12">
                    <FileText size={64} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600 mb-4">Preview not available for this file type.</p>
                    <a
                      href={selectedDocument.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg inline-flex items-center gap-2"
                    >
                      <Download size={18} />
                      Download File
                    </a>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-200">
                <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                  <span>Uploaded by: {selectedDocument.uploaded_by_name}</span>
                  <span>{new Date(selectedDocument.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-slate-600">{selectedDocument.description || 'No description'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
